<template>
  <div
    :ref="`media-${mediaFilename}`"
    class="mc-notification-media__item mc-notification-media__item--show"
    @click="handleClickVideo"
  >
    <VideoPlayer
      v-if="getFormatVideo === 'mp4' || getFormatVideo === 'MP4'"
      :ref="`videoPlayerNotif-${mediaFilename}`"
      class="vjs-custom-skin"
      :options="playerInOptions"
      @ready="onInPlayerReady($event)"
      @error="onInPlayerError($event)"
      @waiting="onInPlayerWaiting($event)"
      @loadeddata="onInPlayerLoadedData($event)"
      @ended="onInPlayerEnded($event)"
    />
    <video-player-mux
      v-else-if="getFormatVideo === 'ts'"
      :ref="`videoPlayerNotif-${mediaFilename}`"
      class="vjs-custom-skin video-js"
      :src="url"
      :playerId="`videoPlayer-${mediaFilename}`"
      @ready="onInPlayerReady($event)"
      @error="onInPlayerError($event)"
      @waiting="onInPlayerWaiting($event)"
      @loadeddata="onInPlayerLoadedData($event)"
      @ended="onInPlayerEnded($event)"
    />
    <span v-if="isPaused" class="mc-notification-media__icon-play-video">
      <i class="mc-fa icon-fill_play"></i>
    </span>
    <div class="mc-notification-media__action" @click.stop>
      <div class="d-flex gap-4 align-items-center">
        <span v-if="isPaused" @click="playVideo()">
          <i class="mc-fa icon-fill_play"></i>
        </span>
        <span v-else @click="pauseVideo()">
          <i class="mc-fa icon-fill_pause"></i>
        </span>
        <span v-if="loadingDownload" class="mc-notification-media__loading-state">
          <b-spinner small />{{ $t('global.processing') }}
        </span>
        <span v-else @click="handleClickDownloadVideo()">
          <i class="mc-fa icon-outline_download"></i> {{ $t('global.action.download') }}
        </span>
      </div>
      <span @click="clickFullscreen()">
        <i class="mc-fa icon-fill_fullscreen" v-if="!fullscreen"></i>
        <i class="mc-fa icon-fill_exit-fullscreen" v-else></i>
      </span>
    </div>
  </div>
</template>
<script>
import { Helper } from '@/mixin/helper'
import VideoPlayer from '@/components/Video/VideoPlayer.vue'
import VideoPlayerMux from '@/components/Video/VideoPlayerMux.vue'
import { MixPanel } from '@/mixin/mixpanel'
import { mapGetters } from 'vuex'

export default {
  components: {
    VideoPlayer,
    VideoPlayerMux,
  },
  mixins: [Helper, MixPanel],
  props: {
    url: {
      type: String,
      required: true,
    },
    mediaFilename: {
      type: String,
      required: true,
    },
  },
  emits: ['play-state-change'],
  data() {
    return {
      isPaused: true,
      fullscreen: false,
      playerInOptions: {
        controls: false,
        controlBar: {
          timeDivider: false,
          durationDisplay: false,
        },
        sources: [
          {
            src: '',
            type: 'video/mp4',
          },
        ],
      },
      loadingDownload: false,
    }
  },

  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    getFormatVideo() {
      if (this.url) {
        const arrUrl = this.url.split('?')
        const arrFormat = arrUrl[0].split('.')
        const format = arrFormat[arrFormat.length - 1]
        return format
      } else {
        return 'mp4'
      }
    },
    refsNameVideoPlayer() {
      return `videoPlayerNotif-${this.mediaFilename}`
    },
  },
  watch: {
    isPaused(newVal) {
      this.$emit('play-state-change', !newVal)
    },
  },
  mounted() {
    if (this.url && (this.getFormatVideo === 'mp4' || this.getFormatVideo === 'MP4')) {
      this.setVideoPlayerOptions(this.url)
    }
  },
  methods: {
    handleClickVideo() {
      if (this.isPaused) {
        this.playVideo()
      } else {
        this.pauseVideo()
      }
    },

    playVideo() {
      this.isPaused = false
      this.$refs[this.refsNameVideoPlayer].player.play()
    },

    pauseVideo() {
      this.isPaused = true
      this.$refs[this.refsNameVideoPlayer].player.pause()
    },

    setVideoPlayerOptions(url) {
      this.playerInOptions.sources[0].src = url
    },

    onInPlayerReady() {
      this.$refs[this.refsNameVideoPlayer].player.pause()
      this.$emit('videoReady')
    },

    onInPlayerError(e) {},

    onInPlayerWaiting() {},

    onInPlayerLoadedData(e) {
      console.log(e)
    },

    onInPlayerEnded() {
      this.$refs[this.refsNameVideoPlayer].player.pause()
      this.isPaused = true
    },

    directDownload() {
      if (this.url) {
        this.downloadFile(this.url, this.mediaFilename)
      }
    },

    async convertAndDownload() {
      this.loadingDownload = true
      const path = this.extractPathFromDomain(this.url)
      try {
        const res = await fetch(process.env.VUE_APP_API_CONVERT_VIDEO + '/' + path, {
          method: 'GET',
        })
        if (!res.ok) throw new Error('Failed to fetch file')
        const blob = await res.blob()
        const downloadUrl = window.URL.createObjectURL(blob)
        let filename = this.mediaFilename
        const contentDisposition = res.headers.get('Content-Disposition')
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1]
          }
        }
        this.downloadFile(downloadUrl, filename)
      } catch (error) {
        this.$mcToast.error('Failed convert video')
        console.error(error)
      } finally {
        this.loadingDownload = false
      }
    },

    handleClickDownloadVideo() {
      this.track('Download video capture alert')
      const hasScopeConvertVideo = this.hasAccess(['b3q-trackvision-convert-video-download:read'])
      if (hasScopeConvertVideo) {
        this.convertAndDownload()
      } else {
        this.directDownload()
      }
    },

    async clickFullscreen() {
      this.fullscreen = !this.fullscreen
      await this.$nextTick()
      const mediaDom = this.$refs[`media-${this.mediaFilename}`]
      mediaDom.classList.toggle('mc-notification-media-video__fullscreen')
      if (this.fullscreen) {
        var elem = document.documentElement

        if (elem.requestFullscreen) {
          elem.requestFullscreen()
        } else if (elem.webkitRequestFullscreen) {
          /* Safari */
          elem.webkitRequestFullscreen()
        } else if (elem.msRequestFullscreen) {
          /* IE11 */
          elem.msRequestFullscreen()
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
          /* Safari */
          document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
          /* IE11 */
          document.msExitFullscreen()
        }
      }
    },
  },
}
</script>

<style lang="scss"></style>
